import { ReactNode, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { MouseTooltip } from '@components/Tooltip';
import styles from './DicomTagTable.module.scss';
import { useTextTooltip } from '@components/Tooltip/useMouseTooltip';

interface DicomTagColumn {
  id: string;
  label: string;
}

interface DicomTagRow {
  [key: string]: ReactNode;
}

interface DicomTagTableProps {
  columns: DicomTagColumn[];
  data: DicomTagRow[];
}

const getTextValue = (value: ReactNode): string | null => {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return null;
};

const hasOverflow = (element: HTMLElement): boolean => {
  return element.scrollHeight > element.clientHeight;
};

function DicomTagTable({ columns, data }: DicomTagTableProps): JSX.Element {
  const [expandedCellKeys, setExpandedCellKeys] = useState<Set<string>>(
    () => new Set(),
  );
  const [collapsibleCellKeys, setCollapsibleCellKeys] = useState<Set<string>>(
    () => new Set(),
  );
  const cellTextRefs = useRef<Map<string, HTMLSpanElement>>(new Map());

  const {
    tooltipNode,
    handleTextMouseEnter,
    handleTextMouseMove,
    handleTextMouseLeave,
  } = useTextTooltip();

  const setCellTextRef = useCallback(
    (cellKey: string, element: HTMLSpanElement | null) => {
      if (element) {
        cellTextRefs.current.set(cellKey, element);
      } else {
        cellTextRefs.current.delete(cellKey);
      }
    },
    [],
  );

  const toggleCell = useCallback((cellKey: string) => {
    setExpandedCellKeys((prev) => {
      const next = new Set(prev);
      if (next.has(cellKey)) {
        next.delete(cellKey);
      } else {
        next.add(cellKey);
      }
      return next;
    });
  }, []);


  useLayoutEffect(() => {
    if (cellTextRefs.current.size === 0) {
      setCollapsibleCellKeys((prev) => (prev.size === 0 ? prev : new Set()));
      return;
    }

    const measured = new Set<string>();
    cellTextRefs.current.forEach((element, cellKey) => {
      if (hasOverflow(element)) {
        measured.add(cellKey);
      }
    });

    setCollapsibleCellKeys((prev) => {
      const next = new Set(prev);
      let changed = false;

      measured.forEach((cellKey) => {
        if (!next.has(cellKey)) {
          next.add(cellKey);
          changed = true;
        }
      });

      const currentKeys = new Set(cellTextRefs.current.keys());
      prev.forEach((cellKey) => {
        if (!currentKeys.has(cellKey)) {
          next.delete(cellKey);
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [columns, data, expandedCellKeys]);

  return (
    <div className={styles.wrapper}>
      <table className={styles.table}>
        <thead className={styles.thead}>
          <tr>
            {columns.map((col) => (
              <th key={col.id} className={styles.th}>
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className={styles.tbody}>
          {data.map((row, rowIndex) => {
            const rowKey = `${row.tag ?? 'row'}-${rowIndex}`;
            return (
              <tr key={rowKey} className={styles.tr}>
                {columns.map((col) => {
                  const value = row[col.id];
                  const textValue = getTextValue(value);
                  const isTextValue = textValue !== null;
                  const cellKey = `${rowKey}-${col.id}`;
                  const isExpanded = expandedCellKeys.has(cellKey);
                  const canCollapse =
                    isTextValue &&
                    (collapsibleCellKeys.has(cellKey) || isExpanded);

                  return (
                    <td key={col.id} className={styles.td}>
                      <div className={styles.cellContent}>
                        <div className={styles.textWrapper}>
                          <span
                            ref={(element) =>
                              setCellTextRef(
                                cellKey,
                                isTextValue ? element : null,
                              )
                            }
                            className={`${styles.cellText} ${isTextValue && !isExpanded ? styles.collapsed : ''} ${
                              canCollapse && !isExpanded
                                ? styles.collapsedOverflow
                                : ''
                            }`}
                            data-name={textValue ?? ''}
                            onMouseEnter={handleTextMouseEnter}
                            onMouseMove={handleTextMouseMove}
                            onMouseLeave={handleTextMouseLeave}
                          >
                            {value}
                          </span>
                          {canCollapse && (
                            <button
                              type="button"
                              className={styles.toggleButton}
                              onClick={() => toggleCell(cellKey)}
                              aria-label={
                                isExpanded ? 'Collapse value' : 'Expand value'
                              }
                            >
                              <span
                                className={`${styles.toggleIcon} ${
                                  isExpanded ? styles.toggleIconExpanded : ''
                                }`}
                                aria-hidden="true"
                              />
                            </button>
                          )}
                        </div>
                      </div>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      {tooltipNode}
    </div>
  );
}

export default DicomTagTable;
